
import Vue, { PropType } from 'vue';
import { cloneDeep } from 'lodash';
import PollAnswer from './PollAnswer.vue';
import PollCountdown from './PollCountdown.vue';
import Api from '@/core/api';
import { Theme } from '@/side-bar/side-bar-config.interface';

const POST_NOTIFICATION_TIMEOUT = 3 * 1000;

export interface Vote {
    text:string,
    id:string,
    selected:boolean
}

export default Vue.extend({
    components: {
        PollAnswer,
        PollCountdown
    },

    props: {
        api: {
            type: Object as PropType<Api>,
            required: true,
        },
        pollId: {
            type: String,
            required: true,
        },
        question: {
            type: String,
            required: true,
        },
        countdownEndTimestamp: {
            type: Number,
            default: -1,
        },
        countdownDuration: {
            type: Number,
            default: -1,
        },
        answers: {
            type: Array,
            required: true,
        },
        labels: {
            type: Object as PropType<Record<string, unknown>>,
            default: () => ({})
        },
        answerLimit: {
            type: Number,
            required: true
        },
        theme: {
            type: Object as PropType<Theme>,
            default: () => ({})
        }
    },

    data() {
        return {
            votes: [] as Vote[],
            sending: false,
            showMessage: false,
            message: '',
            messageTimeout: 0
        };
    },

    computed: {
        multipleAnswersAllowed():boolean {
            return this.answerLimit > 1;
        },
        hasSelectedAnAnswer():boolean {
            return this.selectedAnswers.length > 0;
        },
        reachedMaxAnswers():boolean {
            return this.selectedAnswers.length === this.answerLimit;
        },
        disableUnselected():boolean {
            // if multipleAnswersAllowed = false, we have radio buttons, in which case we never disable them
            return this.multipleAnswersAllowed && this.reachedMaxAnswers;
        },
        selectedAnswers():Array<Vote> {
            return this.votes.filter(v => v.selected);
        },
        answerLimitHint():string {
            return (this.labels.answerLimitHint as string || '').replace('{0}', this.answerLimit.toString());
        },
        disableSubmit(): boolean {
            return this.sending || !this.hasSelectedAnAnswer;
        }
    },

    watch: {
        answers: {
            immediate: true,
            handler() {
                if (this.votes && this.votes.length) {
                    return;
                }
                this.votes = (cloneDeep(this.answers || {}) as Vote[]).map((o: Vote) => {
                    o.selected = false;
                    return o;
                });
            }
        }
    },

    beforeDestroy() {
        clearTimeout(this.messageTimeout);
    },

    methods: {
        onClick(vote:Vote) {
            const newVal = !vote.selected;
            if (!this.multipleAnswersAllowed) {
                this.votes.forEach((v) => v.selected = false);
            }
            vote.selected = newVal;
        },

        submit() {
            this.sending = true;
            this.api.customAction({
                pollId: this.pollId,
                answers: this.selectedAnswers.map(v => v.id),
                onError: (errorMessage: string) => this.onSendingError(errorMessage)
            }, 'sideBarPolls');
        },

        onSendingError(errorMessage:string):void {
            this.sending = false;
            this.displayNewMessage(errorMessage || this.labels.anErrorOccurred as string);
        },

        displayNewMessage(message: string): void {
            if (!message) {
                return;
            }
            clearTimeout(this.messageTimeout);
            this.message = message;
            this.showMessage = true;
            this.messageTimeout = setTimeout(() => this.showMessage = false, POST_NOTIFICATION_TIMEOUT);
        },
    }
});
